
import React, {useState, useEffect, useMemo, useRef} from 'react';
import GoogleMapReact from 'google-map-react';
import Plane from './Plane';
import GeoDesc from './GeoDesc'
import './HeatMap.css';
import './Dot.css';
import './Loading.css';

//Need to work on mobile menu and heatmap

var axios = require("axios").default;

//Max planes drawn at once, keeps the map smooth when zoomed far out
//Over this, half the planes are hidden, then half again, until it fits
const MAX_PLANES = 2500;

//Stable number for a plane's icao24 address, so the same planes stay hidden between refreshes
//(FNV-1a hash, spreads addresses evenly so thinning isn't tied to a country or airline)
const planeHash = (icao) => {
  let hash = 2166136261;
  for (let i = 0; i < icao.length; i++) {
    hash ^= icao.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

//adsb.lol fallback: search radius in nautical miles (250 is the API max)
const ADSB_RADIUS_NM = 250;
//Used if the fallback is needed before the user's location is known (Los Angeles)
const DEFAULT_CENTER = {lat: 34.00, lng: -118.00};
//After the map stops moving for this long, fetch adsb.lol planes around the new center
const PAN_REFRESH_DELAY_MS = 1000;
//Only refetch after a pan if the center moved at least this far from the last search
const PAN_REFRESH_MIN_NM = 25;

//Rough distance in nautical miles between two {lat, lng} points (1 degree of latitude = 60 nm)
const distanceNm = (a, b) => {
  const dLat = (a.lat - b.lat) * 60;
  const dLng = (a.lng - b.lng) * 60 * Math.cos(((a.lat + b.lat) / 2) * Math.PI / 180);
  return Math.sqrt(dLat * dLat + dLng * dLng);
};

//Convert an adsb.lol aircraft into the OpenSky state array layout so the rest of the app is unchanged
//adsb.lol gives altitude in feet ("ground" when landed) and speed in knots, OpenSky uses meters and m/s
const adsbToOpenSky = (ac) => {
  const onGround = ac.alt_baro === 'ground';
  const state = new Array(17).fill(null);
  state[0] = ac.hex;
  state[1] = ac.flight ? ac.flight.trim() : (ac.r || ac.hex);
  state[5] = ac.lon ?? null;
  state[6] = ac.lat ?? null;
  state[7] = onGround ? 0 : (typeof ac.alt_baro === 'number' ? Math.round(ac.alt_baro * 0.3048) : null);
  state[8] = onGround;
  state[9] = typeof ac.gs === 'number' ? Math.round(ac.gs * 0.514444) : null;
  state[10] = ac.track ?? 0;
  return state;
};

const Geolocation = () => {
  const [location, setLocation] = useState({lati:"Loading...", longi:"Loading...", accur:"Loading...", center:null, zoom:null});
  //PART OF USER LOCATION Project
  //const [userLocation, setUserLocation] = useState({minLatitude:null, minLongitude:null, maxLatitude:null, maxLongitude:null});
  //const [location, setLocation] = useState({lati:0, longi:0, accur:0, center:null, zoom:null});
  //const [userLocation, setUserLocation] = useState({minLatitude:30, minLongitude:-125, maxLatitude:42, maxLongitude:-112});
  const [error, setError] = useState('')
  const [plane, setPlane] = useState('');
  const [debouncedPlane, setDebouncedPlane]  = useState(plane);
  //Visible map area, updated whenever the user pans or zooms
  const [mapBounds, setMapBounds] = useState(null);
  //Set once OpenSky runs out of credits so we stop asking it for the rest of the visit
  const openSkyLimitedRef = useRef(false);
  //Latest map center (or user location), used for the adsb.lol radius search
  const searchCenterRef = useRef(null);
  //Center of the last adsb.lol search, and whether the planes on screen came from adsb.lol
  const lastAdsbCenterRef = useRef(null);
  const usingAdsbRef = useRef(false);
  //Pending pan refresh timer, and a counter so an older response can't overwrite a newer one
  const panTimerRef = useRef(null);
  const adsbRequestRef = useRef(0);
  //Makes sure the quick first load only happens once
  const initialLoadRef = useRef(false);
  //Google Maps objects for drawing the coverage circle (googleMaps is state so the circle draws once the map loads)
  const [googleMaps, setGoogleMaps] = useState(null);
  const coverageCircleRef = useRef(null);
  //Center of the adsb.lol planes on screen, null when showing OpenSky (whole world)
  const [adsbCenter, setAdsbCenter] = useState(null);

  //Backup source, only covers a radius around the map center
  const FallbackFlight = async() => {
    const center = searchCenterRef.current || DEFAULT_CENTER;
    const requestId = ++adsbRequestRef.current;
    lastAdsbCenterRef.current = center;
    const response = await axios.get(`/adsb/v2/point/${center.lat.toFixed(4)}/${center.lng.toFixed(4)}/${ADSB_RADIUS_NM}`);
    if (requestId !== adsbRequestRef.current) return;
    setPlane((response.data.ac || []).map(adsbToOpenSky));
    usingAdsbRef.current = true;
    setAdsbCenter(center);
  };

  //Called by the map on every pan/zoom, refreshes adsb.lol planes once the map settles somewhere new
  const handleMapChange = ({center, bounds}) => {
    setMapBounds(bounds);
    searchCenterRef.current = center;
    if (!usingAdsbRef.current || !lastAdsbCenterRef.current) return;
    clearTimeout(panTimerRef.current);
    if (distanceNm(center, lastAdsbCenterRef.current) < PAN_REFRESH_MIN_NM) return;
    panTimerRef.current = setTimeout(() => {
      FallbackFlight().catch((error) => console.error(error));
    }, PAN_REFRESH_DELAY_MS);
  };

  //Quick first load from adsb.lol so planes show up right away instead of waiting on OpenSky
  //If OpenSky answers first, its response invalidates this one (see adsbRequestRef)
  const InitialLoad = () => {
    if (initialLoadRef.current) return;
    initialLoadRef.current = true;
    FallbackFlight().catch((error) => console.error(error));
  };

  //Cancel a pending pan refresh if the component goes away
  useEffect(() => () => clearTimeout(panTimerRef.current), []);

  //Only keep planes inside the visible map area (plus a small margin)
  //If there are more than MAX_PLANES, show every 2nd plane, then every 4th, and so on until it fits,
  //so the whole screen stays covered and busy areas still look busier
  const visiblePlanes = useMemo(() => {
    if (!plane || !mapBounds) return [];
    const {nw, se} = mapBounds;
    const latPad = (nw.lat - se.lat) * 0.1;
    const lngPad = (se.lng - nw.lng) * 0.1;
    const inView = plane.filter((p) =>
      p[6] !== null && p[5] !== null &&
      p[6] <= nw.lat + latPad && p[6] >= se.lat - latPad &&
      p[5] >= nw.lng - lngPad && p[5] <= se.lng + lngPad
    );
    //Keeping planes whose hash divides by 2, 4, 8... means each step hides half of what was left
    let shown = inView;
    for (let step = 2; shown.length > MAX_PLANES; step *= 2) {
      const currentStep = step;
      shown = inView.filter((p) => planeHash(String(p[0])) % currentStep === 0);
    }
    return shown;
  }, [plane, mapBounds]);

  //Circle showing the adsb.lol search area, null when showing OpenSky (whole world)
  const coverage = useMemo(
    () => adsbCenter ? {center: adsbCenter, radiusNm: ADSB_RADIUS_NM} : null,
    [adsbCenter]
  );

  //Draw (or hide) the circle showing the area the drawn planes come from
  useEffect(() => {
    if (!googleMaps) return;
    if (!coverageCircleRef.current) {
      coverageCircleRef.current = new googleMaps.maps.Circle({
        strokeColor: '#f0c463',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#f0c463',
        fillOpacity: 0.05,
        clickable: false,
      });
    }
    const circle = coverageCircleRef.current;
    if (coverage) {
      circle.setCenter(coverage.center);
      circle.setRadius(coverage.radiusNm * 1852);
      circle.setMap(googleMaps.map);
    } else {
      circle.setMap(null);
    }
  }, [googleMaps, coverage]);
  //PART OF HEATMAP Project
  //const [heatmapPoints, setHeatMapPoints] = useState(['']);
  //const [heatmapVisible, setHeatMapVisible] = useState('');
  //const [heatmapData, setHeatMapData] = useState({positions: null, options:{radius: 20, opacity:0.6}});
  
  //Debouncing the text and making a request only once every 30000ms
  useEffect(() => {
    const timerId = setTimeout(() =>{
        setDebouncedPlane(plane);
    }, 30000);
    return () => {
        clearTimeout(timerId);
    };
  },[plane]);

  useEffect(() => {
    const Position = async() => {
      //GEOLOCATION API
      await window.navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log("Geolocation API: ",position);
          if (!searchCenterRef.current) {
            searchCenterRef.current = {lat: position.coords.latitude, lng: position.coords.longitude};
          }
          InitialLoad();
          //Setting Variables
          setLocation({ 
            center: {lat: position.coords.latitude, lng: position.coords.longitude},
            zoom: 8,
            lati:position.coords.latitude,
            longi:position.coords.longitude,
            accur:position.coords.accuracy
          });
        },
      //Setting Error
      (err) => {
        setError(err.message);
        //No location, so the first load searches around DEFAULT_CENTER
        InitialLoad();
      }
      );
  
      console.log("User Location: ",location.lati, location.longi);
      //... allows it to just change one variable of state
      //Showing Latitude
      if(error && ! location.lati){
        setLocation({...location,lati:error})
      }
  
      //Showing Longitude
      if(error && !location.longi){
        setLocation({...location,longi:error})
      }
    
      //Showing Accuracy
      if(error && location.accur==="Loading..."){
        setLocation({...location,accur:error})
      }
      //Need to fix
      if(!error && location.accur!=="Loading..."){
        setLocation({...location,accur: (location.accur)})
      }
  
      //PART OF USER LOCATION Project
      //Add a slider bar so they can add more or less to the distance. Make sure it caps at the lat/long
      //setUserLocation({
      //  minLatitude : location.lati-5,
      //  minLongitude : location.longi-5,
      //  maxLatitude : location.lati+5,
      //  maxLongitude : location.longi+5
      //});
    }  


    const Flight =  async() => {

      const options =  {
        method: 'GET',
        //Contiguous US
        //url: '/api/states/all?lamin=24&lomin=-125&lamax=50&lomax=-66',
        //The World
        url: '/api/states/all',
        //Based on  User Location
        //url: `https://opensky-network.org/api/states/all?lamin=${userLocation.minLatitude}&lomin=${userLocation.minLongitude}&lamax=${userLocation.maxLatitude}&lomax=${userLocation.maxLongitude}`,
      };
      try {
        if (openSkyLimitedRef.current) {
          await FallbackFlight();
          return;
        }
        try {
          const response = await axios.request(options);
          setPlane(response.data.states || []);
          usingAdsbRef.current = false;
          //Drop any adsb.lol response still in flight so it doesn't replace the worldwide data
          adsbRequestRef.current++;
          setAdsbCenter(null);
        } catch (openSkyError) {
          //429 means the daily OpenSky credits are used up, switch to adsb.lol for good
          if (openSkyError.response && openSkyError.response.status === 429) {
            console.warn("OpenSky credits used up, switching to adsb.lol");
            openSkyLimitedRef.current = true;
          } else {
            console.error(openSkyError);
          }
          await FallbackFlight();
        }
      } catch (error) {
        console.error(error);
        //Both sources failed, re-set the old planes so the debounce timer schedules another try
        setPlane((prev) => [...(prev || [])]);
      }
    }
      
  Position();
  Flight();
  //Need to add flight in useEffect parameter but need to do a delay of at least 5-10 seconds to prevent overwork
  //Only re-run on the debounce timer, adding location/error here would refetch on every location update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[debouncedPlane]);

  return(
    <div >
      {console.log("Plane Api Info: " , plane)}
      <GeoDesc 
        lat = {location.lati}
        long = {location.longi}
        acc = {location.accur}
      />
      
      <div className="Loading map-frame">
        {location.center &&

          (<GoogleMapReact
            bootstrapURLKeys={{ key: 'AIzaSyAWWYtcruCxyqXXg4z6eJf_V1wdsyp-ol8' }}
            defaultCenter={location.center}
            defaultZoom={location.zoom}
            onChange={handleMapChange}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={({map, maps}) => setGoogleMaps({map, maps})}
            //heatmapLibrary={true}
            //heatmap={heatmapData}
          >

            {/*This is user location*/}
            <Plane
              planeID = 'User'
              lat = {location.lati.toFixed(3)}
              lng = {location.longi.toFixed(3)}
              color = 'green'
              elev = ' '
              speed = ' '
              key='userExtra'
            />
          
            {/* Plane data indexes
            0: icao24 - Unique ICAO 24bit address of transponder
            1: callsign -
            2: origin_country -
            3: time_pos - 
            4: last_contact - 
            5: longitude - 
            6: latitude -
            7: baro_altitude - 
            8: on_ground - (boolean)
            9: velocity - m/s
            10: true_track - direction
            11: elevation_rate - change in elevation m/s
            12: sensors -
            13: geo_altitude -
            14: squawk - transponder code
            15: spi - special purpose indicator
            16: position_source - Origin of this state’s position: 0=ADS-B, 1=ASTERIX, 2=MLAT
            */}

            {/*Adding Plane React Objects*/}
            {visiblePlanes.map(function(object){
              const colorOption = object[8] === true ? 'blue' : `rgba(${object[7]*255/10000},0,0,1)`;

              //HEATMAP ATTEMPT
              //setHeatMapPoints([ ...heatmapPoints, plane[i][6], plane[i][5]])
              //{setHeatMapData({positions:heatmapPoints})};

              return(
                <Plane
                  planeID = {object[1]}
                  lng = {object[5]}
                  lat = {object[6]}
                  elev = {object[7]}
                  speed = {object[9]}
                  direction={object[10]}
                  color = {colorOption}
                  key={object[0]}
                />

              );
            })}

          </GoogleMapReact>)   
        }
        {/* <button className="toggleButton" onClick={setHeatMapVisible(!heatmapVisible)}>Toggle heatmap</button>*/}
      </div> 

    </div>
  );
}

export default Geolocation;